function stableSerialize(value) {
  if (value === null || typeof value !== 'object') {
    return String(value)
  }

  if (Array.isArray(value)) {
    return '[' + value.map(stableSerialize).join('|') + ']'
  }

  return Object.keys(value)
    .sort()
    .map((key) => key + ':' + stableSerialize(value[key]))
    .join('|')
}

function resolveKey(key) {
  return typeof key === 'string' ? key : stableSerialize(key)
}

function resolveTtl(ttl, fallbackTtl) {
  return typeof ttl === 'number' ? ttl : fallbackTtl
}

export function createMemoizedStateManager(options = {}) {
  const cache = new Map()
  const maxEntries = options.maxEntries ?? 100
  const defaultTtl = options.ttl ?? 0
  let disposed = false
  let accessCounter = 0

  function expiresAt(ttl) {
    const resolvedTtl = resolveTtl(ttl, defaultTtl)

    return resolvedTtl > 0 ? Date.now() + resolvedTtl : 0
  }

  function isExpired(entry) {
    return entry.expiresAt > 0 && entry.expiresAt <= Date.now()
  }

  function prune() {
    if (!cache.size) return

    for (const [key, entry] of cache.entries()) {
      if (isExpired(entry) && !entry.inFlight) {
        cache.delete(key)
      }
    }

    while (cache.size > maxEntries) {
      let oldestKey = null
      let oldestAccess = Infinity

      for (const [key, entry] of cache.entries()) {
        if (!entry.inFlight && entry.lastAccessed < oldestAccess) {
          oldestAccess = entry.lastAccessed
          oldestKey = key
        }
      }

      if (oldestKey === null) break
      cache.delete(oldestKey)
    }
  }

  function get(key) {
    if (disposed) return undefined

    const resolvedKey = resolveKey(key)
    const entry = cache.get(resolvedKey)

    if (!entry || isExpired(entry)) {
      if (entry && !entry.inFlight) {
        cache.delete(resolvedKey)
      }
      return undefined
    }

    entry.lastAccessed = ++accessCounter

    return entry.value
  }

  function set(key, value, setOptions = {}) {
    if (disposed) return value

    const resolvedKey = resolveKey(key)
    cache.set(resolvedKey, {
      value,
      inFlight: null,
      expiresAt: expiresAt(setOptions.ttl),
      lastAccessed: ++accessCounter,
    })
    prune()

    return value
  }

  function memoize(key, loader, memoizeOptions = {}) {
    if (disposed) {
      return Promise.resolve().then(loader)
    }

    const resolvedKey = resolveKey(key)
    const force = memoizeOptions.force === true
    const existing = cache.get(resolvedKey)

    if (!force && existing) {
      if (existing.inFlight) {
        return existing.inFlight
      }

      if (!isExpired(existing)) {
        existing.lastAccessed = ++accessCounter
        return Promise.resolve(existing.value)
      }
    }

    const startedAt = ++accessCounter
    const previousValue = existing ? existing.value : undefined
    const hasPreviousValue = existing && Object.prototype.hasOwnProperty.call(existing, 'value')
    const promise = Promise.resolve()
      .then(loader)
      .then((value) => {
        const transformedValue = typeof memoizeOptions.transform === 'function'
          ? memoizeOptions.transform(value)
          : value

        if (!disposed) {
          set(resolvedKey, transformedValue, { ttl: memoizeOptions.ttl })
        }

        return transformedValue
      })
      .catch((error) => {
        if (!hasPreviousValue) {
          cache.delete(resolvedKey)
        }
        throw error
      })
      .finally(() => {
        const entry = cache.get(resolvedKey)
        if (entry && entry.inFlight === promise) {
          entry.inFlight = null
        }
      })

    cache.set(resolvedKey, {
      value: previousValue,
      inFlight: promise,
      expiresAt: existing ? existing.expiresAt : 0,
      lastAccessed: existing ? existing.lastAccessed : startedAt,
    })
    prune()

    return promise
  }

  function invalidate(key) {
    if (typeof key === 'undefined') {
      cache.clear()
      return
    }

    cache.delete(resolveKey(key))
  }

  function dispose() {
    disposed = true
    cache.clear()
  }

  function size() {
    prune()

    return cache.size
  }

  return {
    get,
    set,
    memoize,
    invalidate,
    dispose,
    size,
  }
}
